import { useMemo } from 'react';
import {
  AccommodationConsoleListingPricingFragment,
  AccommodationConsoleListingPropertyFragment,
  TravelCurrencyCode,
  useAccommodationConsoleListingPricingsQuery
} from '@graphql/generated';
import { useCurrencyFormatter, FormatCurrencyHandler } from '@shared/utils/currency';
import { ListingWithPricing } from '../AccommodationsConsole.types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { getPercentageOff } from '@shared/utils/accommodationPrice';
import { useMinimumPercentageOffToDisplay } from '@shared/hooks/useMinimumPercentageOffToDisplay';

type UseBatchedListingPricesArgs = {
  state: AccommodationsConsoleState;
  listingsWithoutPricing: AccommodationConsoleListingPropertyFragment[];
};

const getCurrencyFormatter = (formatter: FormatCurrencyHandler) => (pricingInfo: { floatingPointDecimalString: string; currency: string }) => {
  return formatter({
    priceFloatingPointDecimalString: pricingInfo.floatingPointDecimalString,
    priceCurrencyCode: pricingInfo.currency,
    formatForm: 'short',
    formatPrecision: 0,
    shouldRoundPriceUp: true
  });
};

export const useBatchedListingPrices = (args: UseBatchedListingPricesArgs) => {
  const { listingsWithoutPricing, state } = args;
  const { formatCurrency } = useCurrencyFormatter();
  const { minimumPercentageOffToDisplayValue } = useMinimumPercentageOffToDisplay();
  const { data: pricingData, loading: isFetchingListingPrices, error } = useAccommodationConsoleListingPricingsQuery({
    variables: {
      payload: {
        joyPlaceIds: listingsWithoutPricing.map(x => x.joyPlaceId),
        currencyCode: TravelCurrencyCode.USD,
        dates: {
          checkIn: {
            high: state.searchDates.start,
            low: state.searchDates.start
          },
          checkOut: {
            high: state.searchDates.end,
            low: state.searchDates.end
          }
        },
        occupancies: state.occupancies
      }
    },
    skip: !listingsWithoutPricing.length,
    batchMode: 'off'
  });

  const listings = useMemo<Array<ListingWithPricing>>(() => {
    if (!pricingData?.travelPlacesPricingAndBookingDetails?.joyPlaces?.length) {
      return listingsWithoutPricing;
    }

    const pricingByJoyPlaceId =
      pricingData.travelPlacesPricingAndBookingDetails.joyPlaces.reduce((acc, place) => {
        acc[place.joyPlace.id] = place.pricingInfo;
        return acc;
      }, {} as Record<string, Maybe<AccommodationConsoleListingPricingFragment>>) || {};

    const formatCurrencyValue = getCurrencyFormatter(formatCurrency);
    return listingsWithoutPricing.map(listing => {
      const pricingInfo = pricingByJoyPlaceId[listing.joyPlaceId];

      if (!pricingInfo) {
        return listing;
      }

      // If the strikeout nightly rate is greater than the nightly base rate,
      // that signifies that the rate is cheaper
      const shouldShowDiscount = (pricingInfo.strikeoutNightlyBaseRate?.valueInMinorUnits ?? 0) > pricingInfo.nightlyBaseRate.valueInMinorUnits;

      return {
        ...listing,
        pricingInfo: {
          discountPercentage: shouldShowDiscount
            ? getPercentageOff(
                minimumPercentageOffToDisplayValue,
                pricingInfo.nightlyBaseRate.valueInMinorUnits,
                pricingInfo.strikeoutNightlyBaseRate?.valueInMinorUnits,
                pricingInfo.nightlyBaseRate.digits,
                pricingInfo.strikeoutNightlyBaseRate?.digits
              )
            : undefined,
          nightlyBaseRateString: formatCurrencyValue(pricingInfo.nightlyBaseRate),
          strikeoutNightlyBaseRateString: shouldShowDiscount && pricingInfo.strikeoutNightlyBaseRate ? formatCurrencyValue(pricingInfo.strikeoutNightlyBaseRate) : undefined,
          strikeoutTotalRateString: pricingInfo.strikeoutTotalRate ? formatCurrencyValue(pricingInfo.strikeoutTotalRate) : undefined,
          totalRateString: formatCurrencyValue(pricingInfo.totalRate),
          nightlyBaseRate: Math.round(pricingInfo.nightlyBaseRate.valueInMinorUnits / Math.pow(10, pricingInfo?.nightlyBaseRate?.digits))
        }
      };
    });
  }, [pricingData, listingsWithoutPricing, formatCurrency, minimumPercentageOffToDisplayValue]);

  return {
    listings,
    isFetchingListingPrices,
    error
  };
};
